/**
 * Global stale-chunk recovery.
 *
 * Catches three error families:
 *   1. unhandled `import()` rejections from lazy() routes that fail
 *      because the chunk URL is gone post-deploy.
 *   2. window.error events from <script> tags that 404 — e.g. the SW
 *      handing us a precached HTML pointing at deleted chunks.
 *   3. React render errors with the same shape, surfaced via the
 *      ErrorBoundary which calls `tryStaleChunkRecovery()` from here
 *      so the loop-guard logic is unified.
 *
 * Loop protection (CRITICAL — otherwise we infinite-reload):
 *   - `?_chunkguard=off` in the URL → guard disabled entirely. The
 *     user can paste this into the address bar to escape a runaway
 *     auto-reload loop.
 *   - A `_v=…` cache-bust param in the URL → we already reloaded once
 *     this session; show the manual recovery UI instead of auto-reloading.
 *   - `sessionStorage[__stale_chunk_reloaded]` flag → same intent,
 *     survives a navigation that strips the query.
 *   - If both signals say "we already tried," `tryStaleChunkRecovery`
 *     returns false and the caller (boundary or window listener) shows
 *     the manual UI.
 */

const RELOAD_FLAG = "__stale_chunk_reloaded";
const KILL_QS = "_chunkguard";
const RELOAD_QS = "_v";

const PATTERNS = [
  /Minified React error #130/i,
  /Element type is invalid/i,
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /error loading dynamically imported module/i,
];

export function looksLikeStaleChunk(error: unknown): boolean {
  if (!error) return false;
  const msg =
    typeof error === "string"
      ? error
      : `${(error as { name?: string }).name ?? ""} ${(error as { message?: string }).message ?? ""}`;
  return PATTERNS.some((re) => re.test(msg));
}

function killSwitchEngaged(): boolean {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(KILL_QS) === "off";
  } catch {
    return false;
  }
}

function alreadyTriedThisSession(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RELOAD_QS)) return true;
  } catch { /* ignore */ }
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === "1") return true;
  } catch { /* ignore */ }
  return false;
}

async function purgeAndReload(): Promise<void> {
  try { sessionStorage.setItem(RELOAD_FLAG, "1"); } catch { /* private mode */ }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch { /* fall through to reload regardless */ }
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_QS, String(Date.now()));
  window.location.replace(url.toString());
}

/**
 * The unified entry point used by ErrorBoundary and the window
 * listeners below. Returns true if a recovery reload was kicked off,
 * false if loop-protection blocked it (caller should fall through to
 * showing manual recovery UI).
 */
export function tryStaleChunkRecovery(error: unknown): boolean {
  if (killSwitchEngaged()) return false;
  if (!looksLikeStaleChunk(error)) return false;
  if (alreadyTriedThisSession()) return false;
  void purgeAndReload();
  return true;
}

/**
 * Exposed for the manual "Clear cache & reload" button in the boundary.
 * Does NOT consult the loop guard — the user explicitly clicked it,
 * they get a reload regardless.
 */
export function manualPurgeAndReload(): void {
  void purgeAndReload();
}

let installed = false;

export function installStaleChunkGuard(): void {
  if (installed) return;
  installed = true;
  (window as unknown as { __chunkGuardInstalled?: boolean }).__chunkGuardInstalled = true;

  if (killSwitchEngaged()) return;

  window.addEventListener("error", (e) => {
    const err = (e as ErrorEvent).error ?? (e as ErrorEvent).message;
    tryStaleChunkRecovery(err);
  });
  window.addEventListener("unhandledrejection", (e) => {
    tryStaleChunkRecovery((e as PromiseRejectionEvent).reason);
  });
}
